import { useEffect, useState } from "react";
import { Trophy, X, ShareNetwork, Copy, Check } from "@phosphor-icons/react";

export default function PRCelebration({ prs, onClose }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (prs?.length > 0) {
      setVisible(true);
      // Generate celebration particles
      const p = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        delay: Math.random() * 0.5,
        size: Math.random() * 6 + 4,
        color: ['#E05A1D', '#F06B2E', '#F59E0B', '#22C55E', '#FFFFFF'][Math.floor(Math.random() * 5)],
        duration: Math.random() * 1.5 + 1,
      }));
      setParticles(p);
    }
  }, [prs]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const shareText = prs?.map(pr =>
    `NEW PR: ${pr.exercise_display} — ${pr.weight}kg${pr.previous_weight > 0 ? ` (was ${pr.previous_weight}kg)` : ""}`
  ).join("\n");

  const handleShare = async () => {
    const text = `Personal Record Broken!\n\n${shareText}\n\n#UpTop #PR #NewRecord`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "UpTop — New PR!", text });
        return;
      } catch (e) { /* fallback */ }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard not available */ }
  };

  if (!prs?.length) return null;

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center transition-all duration-300 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      data-testid="pr-celebration-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />

      {/* Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map(p => (
          <div
            key={p.id}
            className="absolute animate-pr-particle"
            style={{
              left: `${p.x}%`,
              top: '-10px',
              width: `${p.size}px`,
              height: `${p.size}px`,
              backgroundColor: p.color,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Card */}
      <div
        className={`relative z-10 bg-[#161616] border-2 border-[#E05A1D] p-8 max-w-md w-full mx-4 text-center transition-all duration-500 ${
          visible ? "scale-100 translate-y-0" : "scale-90 translate-y-8"
        }`}
        data-testid="pr-celebration-card"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#71717A] hover:text-white transition-colors"
          data-testid="pr-celebration-close"
        >
          <X size={20} />
        </button>

        {/* Trophy animation */}
        <div className="animate-pr-trophy mb-4 inline-block">
          <div className="w-20 h-20 bg-[#E05A1D]/20 flex items-center justify-center mx-auto">
            <Trophy size={40} weight="fill" className="text-[#E05A1D]" />
          </div>
        </div>

        <h2
          className="text-3xl font-black uppercase tracking-tight text-white mb-1"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          New Personal Record!
        </h2>
        <p className="text-sm text-[#71717A] mb-6">You just crushed it!</p>

        <div className="space-y-3 mb-6">
          {prs.map((pr, i) => (
            <div
              key={pr.id || i}
              className="border border-[#E05A1D]/30 bg-[#E05A1D]/5 p-4"
              data-testid={`celebration-pr-${i}`}
            >
              <p className="text-sm font-bold uppercase text-[#A1A1AA] tracking-wider">{pr.exercise_display}</p>
              <div className="flex items-baseline justify-center gap-2 mt-1">
                <span
                  className="text-5xl font-black text-[#E05A1D]"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  {pr.weight}
                </span>
                <span className="text-lg text-[#A1A1AA]">kg</span>
              </div>
              {pr.previous_weight > 0 && (
                <p className="text-xs text-[#22C55E] mt-1 font-bold">
                  +{(pr.weight - pr.previous_weight).toFixed(1)}kg improvement!
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Share button */}
        <button
          onClick={handleShare}
          className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
          data-testid="pr-celebration-share-btn"
        >
          {copied ? (
            <>
              <Check size={18} weight="bold" /> Copied to Clipboard!
            </>
          ) : (
            <>
              <ShareNetwork size={18} weight="bold" /> Share Your PR
            </>
          )}
        </button>
      </div>
    </div>
  );
}
